/**
 * Page "Comment venir" — refonte du bottom sheet en page plein-écran routée.
 *
 * Pourquoi page et pas sheet : le bottom sheet avec drag gesture était instable
 * sur iOS WKWebView (jitter, fermetures accidentelles) et la map Leaflet
 * ne re-rendait pas correctement quand on rouvrait le sheet sans tear-down
 * (problème classique invalidateSize sur container qui réapparaît). En page
 * navigée, le composant remount à chaque entrée, ces deux problèmes disparaissent.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { Capacitor } from '@capacitor/core';
import { AppLauncher } from '@capacitor/app-launcher';
import { useBranding } from '@alterrego/module-branding';
import './AddressPage.css';

delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

type AppKey = 'apple' | 'google' | 'waze' | 'citymapper' | 'uber';

function buildAppUrl(app: AppKey, lat: number, lng: number, name: string): string {
  const n = encodeURIComponent(name);
  switch (app) {
    case 'apple':
      return `https://maps.apple.com/?daddr=${lat},${lng}&saddr=Current+Location`;
    case 'google':
      return `https://maps.google.com/maps?daddr=${lat},${lng}`;
    case 'waze':
      return `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
    case 'citymapper':
      return `https://citymapper.com/directions?endcoord=${lat},${lng}&endname=${n}`;
    case 'uber':
      return `https://m.uber.com/ul/?drop[0]=%7B%22latitude%22:${lat},%22longitude%22:${lng},%22nickname%22:%22${n}%22%7D`;
  }
}

// <a target=_blank> synthétique = plus fiable que window.open en WKWebView,
// laisse iOS résoudre vers l'app native si installée.
function openExternal(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

const NATIVE_APPS: Array<{ key: AppKey; label: string; icon: string; scheme: string | null }> = [
  { key: 'apple',      label: 'Plans',       icon: '/img/plans.webp',       scheme: null },
  { key: 'google',     label: 'Google Maps', icon: '/img/google-maps.webp', scheme: 'comgooglemaps://' },
  { key: 'waze',       label: 'Waze',        icon: '/img/waze.webp',        scheme: 'waze://' },
  { key: 'citymapper', label: 'Citymapper',  icon: '/img/citymapper.webp',  scheme: 'citymapper://' },
  { key: 'uber',       label: 'Uber',        icon: '/img/uber.webp',        scheme: 'uber://' },
];

function bearingDeg(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const l1 = (lat1 * Math.PI) / 180;
  const l2 = (lat2 * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(l2);
  const x = Math.cos(l1) * Math.sin(l2) - Math.sin(l1) * Math.cos(l2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

type OrientationEvent = DeviceOrientationEvent & { webkitCompassHeading?: number };

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  const km = meters / 1000;
  const txt = km.toFixed(1).replace('.', ',').replace(',0', '');
  return `${txt} km`;
}

const ARRIVED_THRESHOLD_M = 15;

function carSpeedKmh(meters: number): number {
  const km = meters / 1000;
  if (km <= 5) return 25;
  if (km <= 30) return 50;
  if (km <= 100) return 80;
  return 95;
}
function pubSpeedKmh(meters: number): number {
  const km = meters / 1000;
  if (km <= 5) return 15;
  if (km <= 30) return 25;
  if (km <= 100) return 60;
  return 110;
}

function travelMinutes(meters: number): { walk: number; bike: number; car: number; pub: number } {
  return {
    walk: Math.max(1, Math.ceil(meters / (5000 / 60))),
    bike: Math.max(1, Math.ceil(meters / (15000 / 60))),
    car:  Math.max(1, Math.ceil(meters / (carSpeedKmh(meters) * 1000 / 60))),
    pub:  Math.max(1, Math.ceil(meters / (pubSpeedKmh(meters) * 1000 / 60))),
  };
}

function formatDuration(min: number): string {
  if (min < 60) return `${min} min`;
  if (min < 24 * 60) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
  }
  const days = Math.floor(min / (24 * 60));
  const h = Math.floor((min % (24 * 60)) / 60);
  return h === 0 ? `${days}j` : `${days}j ${h}h`;
}

const TRANSPORTS: Array<{ key: 'walk' | 'bike' | 'car' | 'pub'; emoji: string; label: string }> = [
  { key: 'walk', emoji: '🚶', label: 'À pied' },
  { key: 'bike', emoji: '🚲', label: 'Vélo' },
  { key: 'car',  emoji: '🚗', label: 'Voiture' },
  { key: 'pub',  emoji: '🚇', label: 'Transports' },
];

export function AddressPage() {
  const navigate = useNavigate();
  // Infos établissement tenant-aware (mêmes sources que HomeScreen). Cette page
  // n'est atteignable que lorsque le tenant a des coordonnées (le CTA « Venir »
  // de l'accueil est masqué sinon), donc lat/lng sont garanties présentes ici.
  const { branding } = useBranding();
  const est = branding.establishment ?? {};
  const name = est.name ?? branding.displayName;
  const address = est.address ?? '';
  const postalCode = est.postalCode ?? '';
  const city = est.city ?? '';
  const latitude = est.latitude ?? 0;
  const longitude = est.longitude ?? 0;
  // Logo : même source que le Home (HomeScreen.tsx) — le wordmark tenant prime
  // sur le logo carré, avec repli sur l'asset générique. Sans wordmark (Sauf
  // Imprévu…), on retombe sur `est.logo`, donc rendu inchangé.
  const logo = est.logoWordmark || est.logo || branding.assets?.logo || '';
  const isWordmark = Boolean(est.logoWordmark);
  const fullAddress = `${address}, ${postalCode} ${city}`;

  const mapHostRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const [copied, setCopied] = useState(false);
  const [userPos, setUserPos] = useState<{ lat: number; lng: number } | null>(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [availableApps, setAvailableApps] = useState<AppKey[]>(() => NATIVE_APPS.map((a) => a.key));

  // Filtrage iOS via canOpenUrl ; sur Android/web on garde tout. Fallback dégradé
  // si seul Plans probe (plugin natif manquant ou whitelist Info.plist absente).
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return;
    let cancelled = false;
    (async () => {
      const probed = await Promise.all(
        NATIVE_APPS.map(async (app) => {
          if (!app.scheme) return app.key;
          try {
            const { value } = await AppLauncher.canOpenUrl({ url: app.scheme });
            return value ? app.key : null;
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      const keys = probed.filter((k): k is AppKey => k !== null);
      if (keys.length <= 1) {
        setAvailableApps(NATIVE_APPS.map((a) => a.key));
      } else {
        setAvailableApps(keys);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Geoloc continue tant que la page est montée. clearWatch au démontage.
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  // Compass : iOS requestPermission requis (bouton "Activer"), ailleurs auto.
  type CompassState = 'unsupported' | 'idle' | 'granted' | 'denied';
  const [compassState, setCompassState] = useState<CompassState>('unsupported');
  const [heading, setHeading] = useState<number | null>(null);
  const orientationHandlerRef = useRef<((ev: OrientationEvent) => void) | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof DeviceOrientationEvent === 'undefined') {
      setCompassState('unsupported');
      return;
    }
    const reqPerm = (DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission;
    if (typeof reqPerm === 'function') {
      setCompassState('idle');
    } else {
      attachOrientationListener();
      setCompassState('granted');
    }
    return () => detachOrientationListener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function attachOrientationListener() {
    if (orientationHandlerRef.current) return;
    const handler = (ev: OrientationEvent) => {
      if (typeof ev.webkitCompassHeading === 'number') {
        setHeading(ev.webkitCompassHeading);
        return;
      }
      if (typeof ev.alpha === 'number') setHeading(360 - ev.alpha);
    };
    orientationHandlerRef.current = handler;
    window.addEventListener('deviceorientation', handler as EventListener, true);
  }
  function detachOrientationListener() {
    if (orientationHandlerRef.current) {
      window.removeEventListener('deviceorientation', orientationHandlerRef.current as EventListener, true);
      orientationHandlerRef.current = null;
    }
  }

  const enableCompass = async () => {
    const reqPerm = (DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission;
    if (typeof reqPerm !== 'function') return;
    try {
      const state = await reqPerm();
      if (state === 'granted') {
        attachOrientationListener();
        setCompassState('granted');
      } else {
        setCompassState('denied');
      }
    } catch {
      setCompassState('denied');
    }
  };

  const distanceMeters = useMemo(() => {
    if (!userPos) return null;
    return haversineMeters(userPos.lat, userPos.lng, latitude, longitude);
  }, [userPos, latitude, longitude]);

  const arrowRotation = useMemo(() => {
    if (!userPos || heading === null) return null;
    const bearing = bearingDeg(userPos.lat, userPos.lng, latitude, longitude);
    return bearing - heading;
  }, [userPos, heading, latitude, longitude]);

  const times = useMemo(() => {
    if (distanceMeters === null) return null;
    return travelMinutes(distanceMeters);
  }, [distanceMeters]);

  // Init Leaflet une fois au mount. Plus de toggle open/close foireux puisque
  // la page remount à chaque navigation.
  useEffect(() => {
    if (!mapHostRef.current || mapInstanceRef.current) return;
    const map = L.map(mapHostRef.current, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
    }).setView([latitude, longitude], 16);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);
    L.marker([latitude, longitude]).addTo(map).bindPopup(name);
    mapInstanceRef.current = map;
    // Ceinture+bretelles : invalidateSize après que le layout ait posé les
    // dimensions finales (transitions/safe-area).
    const t = setTimeout(() => map.invalidateSize(), 50);
    return () => {
      clearTimeout(t);
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [latitude, longitude, name]);

  // Recalcule les tuiles quand la map change de taille (toggle expand).
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const t = setTimeout(() => mapInstanceRef.current?.invalidateSize(), 220);
    return () => clearTimeout(t);
  }, [mapExpanded]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // pas de fallback : iOS Capacitor OK, ailleurs on laisse passer
    }
  };

  const handleShare = () => {
    const shareUrl = `https://maps.apple.com/?q=${encodeURIComponent(name)}&ll=${latitude},${longitude}`;
    if (navigator.share) {
      navigator.share({
        title: name,
        text: `${name} — ${fullAddress}`,
        url: shareUrl,
      }).catch(() => {});
    } else {
      handleCopy();
    }
  };

  return (
    <div className="ap-page">
      <header className="ap-header">
        <button className="ap-back" onClick={() => navigate(-1)} aria-label="Retour">‹</button>
        <span className="ap-title">Comment venir</span>
        <div style={{ width: 32 }} />
      </header>

      <div className="ap-destination-card">
        {logo && (
          <img
            className={isWordmark ? 'ap-logo ap-logo-wordmark' : 'ap-logo'}
            src={logo}
            alt={name}
          />
        )}
        <div className="ap-destination-text">
          <div className="ap-destination-name">{name}</div>
          <div className="ap-destination-addr">{fullAddress}</div>
          {distanceMeters !== null && (
            distanceMeters < ARRIVED_THRESHOLD_M ? (
              <div className="ap-arrived">✓ Vous êtes arrivé !</div>
            ) : (
              <div className="ap-distance">à {formatDistance(distanceMeters)}</div>
            )
          )}
        </div>
      </div>

      <div className={`ap-content${mapExpanded ? ' ap-content--map-expanded' : ''}`}>
        <div className={`ap-map-wrap${mapExpanded ? ' ap-map-wrap--expanded' : ''}`}>
          <div className="ap-map" ref={mapHostRef} />
          <button
            className="ap-map-toggle"
            onClick={() => setMapExpanded((v) => !v)}
            aria-label={mapExpanded ? 'Réduire la carte' : 'Agrandir la carte'}
          >
            {mapExpanded ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
          {compassState === 'idle' && (
            <button className="ap-compass ap-compass--idle" onClick={enableCompass}>
              <span className="ap-compass-needle" aria-hidden="true">🧭</span>
              <span className="ap-compass-label">Boussole</span>
            </button>
          )}
          {compassState === 'granted' && userPos && (
            <div className="ap-compass" aria-label="Direction vers le resto">
              <svg viewBox="0 0 48 48" className="ap-compass-arrow" style={{
                transform: arrowRotation !== null ? `rotate(${arrowRotation}deg)` : undefined,
              }}>
                <polygon points="24,6 16,30 24,26 32,30" fill="var(--color-primary, #1464B4)" />
                <polygon points="24,42 16,30 24,34 32,30" fill="rgba(20,100,180,0.35)" />
              </svg>
            </div>
          )}
        </div>

        <div className="ap-transports-row">
          {TRANSPORTS.map((tr) => (
            <div key={tr.key} className="ap-transport-chip">
              <span className="ap-transport-emoji" aria-hidden="true">{tr.emoji}</span>
              <span className="ap-transport-time">
                {times ? formatDuration(times[tr.key]) : '—'}
              </span>
            </div>
          ))}
        </div>

        <div className="ap-apps-grid">
          {NATIVE_APPS.filter((app) => availableApps.includes(app.key)).map((app) => (
            <button
              key={app.key}
              className="ap-app-btn"
              onClick={() => openExternal(buildAppUrl(app.key, latitude, longitude, name))}
            >
              <img src={app.icon} alt="" className="ap-app-icon" />
              <span className="ap-app-label">{app.label}</span>
            </button>
          ))}
        </div>

        <div className="ap-actions-row">
          <button className="ap-action-btn" onClick={handleCopy}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
            </svg>
            <span>{copied ? 'Copié !' : "Copier l'adresse"}</span>
          </button>
          <button className="ap-action-btn" onClick={handleShare}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            <span>Partager</span>
          </button>
        </div>
      </div>
    </div>
  );
}
